import React, { useState, useCallback, useEffect, useRef } from "react";
import type { AppAdapter, SelectionInfo } from "../types";
import { isOfficeAdapter } from "../types";
import type { OfficeAdapter } from "../types";
import type { CaiAgentConfig } from "../ai";
import { useAgent } from "../hooks/useAgent";
import { useChatStore } from "../store/chatStore";
import { useSessionStore } from "../store/sessionStore";
import { useSettingsStore } from "../store/settingsStore";
import { Header } from "./Header";
import { MessageList } from "./MessageList";
import { ChatInput } from "./ChatInput";
import { ErrorBanner } from "./ErrorBanner";
import { SettingsPanel } from "./SettingsPanel";
import { SelectionActionBar } from "./SelectionActionBar";
import { caiApi } from "../api";

interface ChatInterfaceProps {
  adapter: AppAdapter;
  agentConfig: Omit<CaiAgentConfig, "tools" | "modelId" | "maxSteps">;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  mcpTools?: Record<string, any>;
  mcpError?: string | null;
  userName?: string | null;
  contextName?: string;
}

export function ChatInterface({
  adapter,
  agentConfig,
  mcpTools,
  mcpError,
  userName,
}: ChatInterfaceProps) {
  const { messages, isStreaming, sendMessage, error, retryLastMessage, clearMessages, setMessages } =
    useAgent({ adapter, agentConfig, mcpTools });

  const [showSidebar, setShowSidebar] = useState(false);
  const [selection, setSelection] = useState<SelectionInfo | null>(null);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [uploadedFileNames, setUploadedFileNames] = useState<string[]>([]);
  const clearError = useChatStore((s) => s.setError);
  const { activeSessionId, createSession } = useSessionStore();
  const theme = useSettingsStore((s) => s.theme);
  const isStreamingRef = useRef(isStreaming);
  isStreamingRef.current = isStreaming;

  const appName = adapter.appName ?? "CAi";
  const sessionType = adapter.sessionType;

  // Apply theme to root element
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
  }, [theme]);

  // Subscribe to selection changes (Office adapters only)
  useEffect(() => {
    if (!isOfficeAdapter(adapter)) return;
    const officeAdapter = adapter as OfficeAdapter;
    if (!officeAdapter.onSelectionChanged) return;
    const unsubscribe = officeAdapter.onSelectionChanged((sel) => setSelection(sel));
    return unsubscribe;
  }, [adapter]);

  // Poll for @@ prompt markers every 2 seconds (Office adapters only)
  useEffect(() => {
    if (!isOfficeAdapter(adapter)) return;
    const officeAdapter = adapter as OfficeAdapter;
    if (!officeAdapter.processPromptMarkers) return;

    const interval = setInterval(() => {
      if (isStreamingRef.current) return;
      officeAdapter.processPromptMarkers!().then((markers) => {
        for (const marker of markers) {
          const prompt = marker.surroundingContext
            ? `The user typed this prompt inline in the document: "${marker.prompt}"\n\nSurrounding document context:\n${marker.surroundingContext}\n\nPlease fulfill the prompt and replace the placeholder paragraph (currently showing "⏳ Generating...") with the generated content using the execute_office_js tool. Search for "⏳ Generating..." and replace it with the result.`
            : `The user typed this prompt inline in the document: "${marker.prompt}"\n\nPlease fulfill the prompt and replace the placeholder paragraph (currently showing "⏳ Generating...") with the generated content using the execute_office_js tool. Search for "⏳ Generating..." and replace it with the result.`;
          sendMessage(prompt);
        }
      }).catch(() => {
        // Ignore marker scanning errors
      });
    }, 2000);

    return () => clearInterval(interval);
  }, [adapter, sendMessage]);

  const handleDismissError = useCallback(() => clearError(null), [clearError]);

  const handleNewChat = useCallback(async () => {
    const contextId = adapter.getContextId
      ? await adapter.getContextId()
      : undefined;
    await createSession(sessionType, contextId);
    clearMessages();
    setPendingFiles([]);
    setUploadedFileNames([]);
  }, [adapter, sessionType, createSession, clearMessages]);

  const handleSuggestionClick = useCallback(
    (cmd: { promptTemplate: string }) => {
      sendMessage(cmd.promptTemplate.replace("{input}", ""));
    },
    [sendMessage]
  );

  const handleSelectionAction = useCallback(
    (instruction: string, selectedText: string) => {
      const prompt = `The user has selected the following text in the document:\n\n"${selectedText}"\n\nTheir instruction: ${instruction}\n\nPlease apply this instruction to the selected text in the document. Use the execute_office_js tool to find and modify the selected text. Preserve the document's formatting.`;
      sendMessage(prompt);
      setSelection(null);
    },
    [sendMessage]
  );

  // Ensure a session exists (create one if needed) and return the session ID
  const ensureSessionId = useCallback(async (): Promise<string> => {
    if (activeSessionId) return activeSessionId;
    const contextId = adapter.getContextId
      ? await adapter.getContextId()
      : undefined;
    const session = await createSession(sessionType, contextId);
    return session.id;
  }, [activeSessionId, adapter, sessionType, createSession]);

  // Upload files immediately when selected
  const handleFilesSelected = useCallback(
    async (files: File[]) => {
      setPendingFiles((prev) => [...prev, ...files]);
      setUploadingFiles(true);
      try {
        const sessionId = await ensureSessionId();
        await caiApi.uploadFiles(sessionId, files);
        const newNames = files.map((f) => f.name);
        const fileNames = newNames.join(", ");
        setUploadedFileNames((prev) => [...prev, ...newNames]);
        // Replace pending with uploaded state
        setPendingFiles((prev) =>
          prev.map((f) =>
            files.includes(f) ? Object.assign(f, { _uploaded: true }) : f
          )
        );
        // Notify in chat via a user message
        sendMessage(`Uploaded: ${fileNames}`);
      } catch (err) {
        useChatStore.getState().setError(
          `File upload failed: ${err instanceof Error ? err.message : String(err)}`
        );
      } finally {
        setUploadingFiles(false);
        setPendingFiles([]);
      }
    },
    [ensureSessionId, sendMessage]
  );

  const handleRemoveFile = useCallback((index: number) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  return (
    <div className="h-full flex flex-col bg-surface cai-mesh-bg font-sans text-text-primary relative">
      <Header
        appName={appName}
        userName={userName}
        onNewChat={handleNewChat}
        onSidebarToggle={() => setShowSidebar(!showSidebar)}
        showingSidebar={showSidebar}
      />

      {mcpError && (
        <ErrorBanner
          message={`Server tools unavailable: ${mcpError}`}
          onDismiss={handleDismissError}
        />
      )}

      {error && (
        <ErrorBanner
          message={error}
          onDismiss={handleDismissError}
          onRetry={retryLastMessage}
        />
      )}

      <MessageList
        messages={messages}
        isStreaming={isStreaming}
        suggestions={adapter.slashCommands}
        onSuggestionClick={handleSuggestionClick}
      />

      {selection && !isStreaming && (
        <SelectionActionBar
          selection={selection}
          onSubmit={handleSelectionAction}
          onDismiss={() => setSelection(null)}
          disabled={isStreaming}
        />
      )}

      <ChatInput
        onSend={sendMessage}
        disabled={isStreaming}
        slashCommands={adapter.slashCommands}
        onFilesSelected={handleFilesSelected}
        pendingFiles={pendingFiles}
        onRemoveFile={handleRemoveFile}
        uploadingFiles={uploadingFiles}
        uploadedFileNames={uploadedFileNames}
      />

      {showSidebar && (
        <SettingsPanel
          adapter={adapter}
          onClose={() => setShowSidebar(false)}
          onClearMessages={clearMessages}
          onLoadMessages={setMessages}
        />
      )}
    </div>
  );
}
